/**
 * Utilidades para comparación eficiente de rooms
 * Reemplaza JSON.stringify() por comparación de campos clave
 */

/**
 * Compara dos rooms para detectar cambios relevantes
 * @param {Object} oldRoom - Room anterior
 * @param {Object} newRoom - Room nuevo
 * @returns {boolean} - true si el room cambió
 */
export function hasRoomChanged(oldRoom, newRoom) {
    if (!oldRoom || !newRoom) return true;

    // Comparación rápida por campos clave (O(1))
    return (
        oldRoom.unreadCount !== newRoom.unreadCount ||
        oldRoom.roomName !== newRoom.roomName ||
        oldRoom.lastMessage?.timestamp !== newRoom.lastMessage?.timestamp ||
        oldRoom.lastMessage?.content !== newRoom.lastMessage?.content ||
        oldRoom.tags?.length !== newRoom.tags?.length ||
        oldRoom.userSatisfaction !== newRoom.userSatisfaction ||
        oldRoom.escalated_to !== newRoom.escalated_to
    );
}

/**
 * Crea un índice Map de rooms para búsquedas O(1)
 * @param {Array} rooms - Array de rooms
 * @returns {Map} - Map con roomId como key
 */
export function createRoomIndex(rooms) {
    if (!Array.isArray(rooms)) return new Map();
    return new Map(rooms.map(room => [room.roomId, room]));
}

/**
 * Detecta cambios entre dos arrays de rooms de manera eficiente
 * @param {Array} oldRooms - Array anterior
 * @param {Array} newRooms - Array nuevo
 * @returns {Object} - { changedRoomIds: Set, newRoomIds: Set, removedRoomIds: Set }
 */
export function detectRoomChanges(oldRooms, newRooms) {
    const oldRoomsMap = createRoomIndex(oldRooms);
    const newRoomsMap = createRoomIndex(newRooms);

    const changedRoomIds = new Set();
    const newRoomIds = new Set();
    const removedRoomIds = new Set();

    // Detectar rooms nuevos o modificados
    for (const newRoom of newRooms) {
        const oldRoom = oldRoomsMap.get(newRoom.roomId);
        if (!oldRoom) {
            newRoomIds.add(newRoom.roomId);
        } else if (hasRoomChanged(oldRoom, newRoom)) {
            changedRoomIds.add(newRoom.roomId);
        }
    }

    // Detectar rooms removidos
    for (const oldRoom of oldRooms) {
        if (!newRoomsMap.has(oldRoom.roomId)) {
            removedRoomIds.add(oldRoom.roomId);
        }
    }

    return { changedRoomIds, newRoomIds, removedRoomIds };
}

/**
 * Verifica si dos arrays tienen el mismo orden
 * @param {Array} arr1 - Primer array
 * @param {Array} arr2 - Segundo array
 * @returns {boolean} - true si el orden es diferente
 */
export function hasOrderChanged(arr1, arr2) {
    if (!arr1 || !arr2 || arr1.length !== arr2.length) return true;
    
    for (let i = 0; i < arr1.length; i++) {
        if (arr1[i]?.roomId !== arr2[i]?.roomId) {
            return true;
        }
    }
    
    return false;
}
